import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CommonRootState } from '@common/types/common-root-state-type';
import { DBUserMessageThunks, userMessagesSelectByQuery } from '@common-reducers/DBServiceThunks';
import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { AccountPage } from '@common-components/account-page/account-page';
import { ImportMessagesTable, ImportMessagesHeader, ExcelImportDialog } from './components';

export const ImportMessagesView = () => {
  const dispatch = useAppDispatch();
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState<keyof UserMessage>('dueDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filters, setFilters] = useState<Partial<Record<keyof UserMessage, string>>>({});
  const [isLoading, setIsLoading] = useState(true);

  // Load messages from Redux - load all messages first, then paginate locally
  useEffect(() => {
    const loadMessages = async () => {
      setIsLoading(true);
      try {
        await dispatch(DBUserMessageThunks.find({
          $or: [
            { deleted: { $exists: false } },
            { deleted: false }
          ],
          $sort: { dueDate: -1 }
        })).unwrap();
      } catch (error) {
        console.error('Failed to load messages:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadMessages();
  }, [dispatch]);

  // Select messages from Redux store using the same pattern as UserMessageReducer
  const allMessages = useSelector((state: CommonRootState) => 
    userMessagesSelectByQuery(state.DBUserMessageReducer, {
      $or: [
        { deleted: { $exists: false } },
        { deleted: false }
      ]
    })
  );

  // Apply local filtering
  const filteredMessages = useMemo(() => {
    let filtered = [...allMessages];
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        filtered = filtered.filter(msg => {
          const fieldValue = msg[key as keyof UserMessage];
          if (typeof fieldValue === 'string') {
            return fieldValue.toLowerCase().includes(value.toLowerCase());
          }
          return String(fieldValue).toLowerCase().includes(value.toLowerCase());
        });
      }
    });

    return filtered;
  }, [allMessages, filters]);

  const handleSort = useCallback((field: keyof UserMessage) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  const handleFilterChange = useCallback((field: keyof UserMessage, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleImportSuccess = useCallback(async () => {
    // Refresh messages after import
    try {
      await dispatch(DBUserMessageThunks.find({
        $or: [
          { deleted: { $exists: false } },
          { deleted: false }
        ],
        $sort: { dueDate: -1 }
      })).unwrap();
    } catch (error) {
      console.error('Failed to refresh messages after import:', error);
    }
  }, [dispatch]);

  return (
    <AccountPage
      page={
        <div className="flex flex-col h-full">
          <ImportMessagesHeader
            onImport={() => setIsImportDialogOpen(true)}
            messages={filteredMessages}
          />
          
          <div className="flex-1 overflow-hidden">
            <ImportMessagesTable
              messages={filteredMessages}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              filters={filters}
              onFilterChange={handleFilterChange}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              isLoading={isLoading}
            />
          </div>

          <ExcelImportDialog
            open={isImportDialogOpen}
            onClose={() => setIsImportDialogOpen(false)}
            onImportSuccess={handleImportSuccess}
          />
        </div>
      }
    />
  );
};

